// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import RichTextEditor from '../components/RichTextEditor';

const TYPE_META = {
  builtin:      { label: 'Built-in module',     desc: 'Ships with the portal. The contents are managed in the codebase; only the display label, description, and order can be changed here.' },
  content_pdf:  { label: 'PDF document',        desc: 'Clients see a page with the uploaded PDF embedded for viewing.' },
  content_html: { label: 'Rich-text page',      desc: 'Clients see a page with the rich-text content rendered.' },
};

export default function AdminModuleEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  // Editable fields
  const [label,       setLabel]       = useState('');
  const [description, setDescription] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [saving,      setSaving]      = useState(false);
  const [activating,  setActivating]  = useState(false);

  // PDF preview blob URL — built on demand, revoked on unmount
  const [pdfUrl,    setPdfUrl]    = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Two-step delete: step 0 = normal Delete button, step 1 = type-to-confirm
  const [deleteStep,    setDeleteStep]    = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting,      setDeleting]      = useState(false);

  function load() {
    setLoading(true);
    adminApi.getModule(id)
      .then(data => {
        setM(data);
        setLabel(data.label || '');
        setDescription(data.description || '');
        setHtmlContent(data.html_content || '');
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }

  useEffect(load, [id]);

  // Build a blob URL for PDF preview. We have to download the bytes via the
  // adminDownload helper because admin auth uses a header, which <iframe src>
  // can't carry. Object URL is revoked on unmount or module change.
  useEffect(() => {
    if (!m || m.module_type !== 'content_pdf' || !m.pdf_filename) {
      setPdfUrl(null);
      return undefined;
    }
    let revoked = false;
    let currentUrl = null;
    adminApi.fetchModulePdfBlob(m.id)
      .then(blob => {
        if (revoked) return;
        currentUrl = URL.createObjectURL(blob);
        setPdfUrl(currentUrl);
      })
      .catch(() => { /* nothing to preview */ });
    return () => {
      revoked = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [m]);

  async function handleSave(e) {
    e?.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      const fields = {
        label,
        description: description || null,
      };
      if (m.module_type === 'content_html') {
        fields.html_content = htmlContent;
      }
      const updated = await adminApi.updateModule(m.id, fields);
      setM(updated);
      setSuccess('Saved.');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleUploadPdf(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Only PDF files are accepted.');
      return;
    }
    setUploading(true); setError(''); setSuccess('');
    try {
      const updated = await adminApi.uploadModulePdf(m.id, file);
      setM(updated);
      setSuccess(`Uploaded ${file.name}.`);
      setTimeout(() => setSuccess(''), 2000);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) { setError(err.message); }
    finally { setUploading(false); }
  }

  async function handleToggleActive() {
    setActivating(true);
    try {
      const updated = m.is_active
        ? await adminApi.deactivateModule(m.id)
        : await adminApi.activateModule(m.id);
      setM(prev => ({ ...prev, ...updated }));
    } catch (err) { setError(err.message); }
    finally { setActivating(false); }
  }

  function startDelete() { setDeleteStep(1); setDeleteConfirm(''); setError(''); }
  function cancelDelete() { setDeleteStep(0); setDeleteConfirm(''); }

  async function handleDelete() {
    setDeleting(true); setError('');
    try {
      await adminApi.deleteModule(m.id);
      navigate('/admin/dashboard/modules');
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  }

  if (loading) return <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>;
  if (!m)      return <div className="alert alert-error">{error || 'Module not found'}</div>;

  const typeMeta = TYPE_META[m.module_type] || TYPE_META.builtin;
  const canDelete = m.is_deletable !== false;
  const deleteEnabled = deleteConfirm.trim().toLowerCase() === (m.label || '').trim().toLowerCase();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <button className="btn btn-sm" onClick={() => navigate('/admin/dashboard/modules')}>← All Modules</button>
      </div>

      <h1 className="page-title">{m.label}</h1>
      <p className="page-subtitle" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <span>{typeMeta.label}</span>
        {m.key && (
          <span>
            Key: <code style={{ fontSize: 12, background: '#eee', padding: '1px 6px', borderRadius: 3 }}>{m.key}</code>
          </span>
        )}
        <span>{m.active_client_count ?? 0} active client{m.active_client_count === 1 ? '' : 's'}</span>
      </p>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* ── Active status (own row so the toggle never fights with Save) ── */}
      <div className="section-head" style={{ marginTop: 0 }}>Status</div>
      <div className="card">
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 16, flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, fontWeight: 500 }}>
              {m.is_active ? 'Active' : 'Hidden'}
            </div>
            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginTop: 2 }}>
              {m.is_active
                ? 'Visible to clients who have this module assigned.'
                : 'Hidden from every client, even those currently assigned to it.'}
            </div>
          </div>
          <button
            type="button"
            onClick={handleToggleActive}
            disabled={activating}
            aria-label={m.is_active ? 'Deactivate module' : 'Activate module'}
            style={{
              width: 56, height: 30, borderRadius: 15,
              background: m.is_active ? '#0F1838' : '#ddd',
              border: 'none', cursor: activating ? 'default' : 'pointer',
              position: 'relative', flexShrink: 0,
            }}>
            <span style={{
              position: 'absolute', top: 3,
              left: m.is_active ? 29 : 3,
              width: 24, height: 24, borderRadius: '50%',
              background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
        </div>
      </div>

      {/* ── Metadata form ─────────────────────────────────────────────────── */}
      <div className="section-head">Module Information</div>
      <div className="card">
        <p style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555', marginTop: 0, marginBottom: 16 }}>
          {typeMeta.desc}
        </p>

        <form onSubmit={handleSave}>
          <div className="form-group">
            <label>Label</label>
            <input type="text" value={label} onChange={e => setLabel(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Shown on the client's dashboard card" />
          </div>

          {/* HTML content editor (only for content_html) */}
          {m.module_type === 'content_html' && (
            <div className="form-group">
              <label>Page Contents</label>
              <RichTextEditor value={htmlContent} onChange={setHtmlContent} />
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* ── PDF upload (only for content_pdf) ───────────────────────────── */}
      {m.module_type === 'content_pdf' && (
        <>
          <div className="section-head">PDF File</div>
          <div className="card">
            {m.pdf_filename ? (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, wordBreak: 'break-word' }}>
                  <strong>{m.pdf_filename}</strong>
                  <span style={{ color: '#888', marginLeft: 8 }}>
                    ({((m.pdf_size_bytes || 0) / 1024).toFixed(0)} KB)
                  </span>
                </div>
              </div>
            ) : (
              <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#B85042', marginTop: 0, marginBottom: 16 }}>
                No PDF uploaded yet. Clients assigned to this module won't see anything until you upload one.
              </p>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <label htmlFor="module-pdf-input" className="btn" style={{ display: 'inline-block', cursor: 'pointer' }}>
                {uploading ? 'Uploading...' : (m.pdf_filename ? 'Replace PDF' : 'Upload PDF')}
              </label>
              <input
                ref={fileInputRef}
                id="module-pdf-input"
                type="file"
                accept="application/pdf"
                onChange={handleUploadPdf}
                disabled={uploading}
                style={{ display: 'none' }}
              />
              <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888' }}>
                Max 25 MB &middot; PDF only
              </span>
            </div>

            {pdfUrl && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                  Preview
                </div>
                <iframe
                  src={pdfUrl}
                  title="PDF preview"
                  style={{ width: '100%', height: 600, border: '1px solid #ddd', borderRadius: 4 }}
                />
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Danger Zone ──────────────────────────────────────────────────── */}
      <div className="section-head" style={{ color: '#B85042' }}>Danger Zone</div>
      <div className="card" style={{ borderColor: '#f5c6c3' }}>
        {!canDelete ? (
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginTop: 0, marginBottom: 0 }}>
            This is a built-in module and cannot be deleted &mdash; the React app still references its key.
            You can <strong>deactivate</strong> it above to hide it from all clients instead.
          </p>
        ) : deleteStep === 0 ? (
          <>
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginTop: 0, marginBottom: 16 }}>
              Permanently delete this module. This removes the module from all {m.active_client_count ?? 0} client{m.active_client_count === 1 ? '' : 's'} currently assigned to it,
              and from every case-type template that includes it. The PDF or rich-text contents are also discarded. This cannot be undone.
            </p>
            <button className="btn" style={{ borderColor: '#B85042', color: '#B85042' }}
              onClick={startDelete}>
              Delete Module
            </button>
          </>
        ) : (
          <div style={{ padding: 12, border: '1px solid #B85042', borderRadius: 4, background: '#fdf0ef' }}>
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#B85042', margin: '0 0 12px 0', fontWeight: 500 }}>
              Type the module name <strong>"{m.label}"</strong> to confirm permanent deletion.
            </p>
            <input
              type="text"
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              placeholder={m.label}
              autoFocus
              style={{ marginBottom: 12 }}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn" onClick={cancelDelete} disabled={deleting}>Cancel</button>
              <button
                onClick={handleDelete}
                disabled={deleting || !deleteEnabled}
                style={{
                  background: deleteEnabled ? '#B85042' : '#e0a09a', color: '#fff', border: 'none',
                  borderRadius: 3, padding: '8px 16px',
                  cursor: deleting || !deleteEnabled ? 'default' : 'pointer',
                  fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 500,
                }}>
                {deleting ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
